/**
 * AdminGenerationGallery — Cross-project generation admin view with
 *   • left sidebar tree (Project → Building → Floor → leaf count)
 *   • top filter chips (view_type, period, search)
 *   • center grid of rendered generations
 *   • sticky KPI bar (total / this week / failures placeholder / consumption)
 *
 * Used inside GenerationPage as a new tab "Galerie".
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import {
  ChevronDown, ChevronRight, Building2, Layers, FolderOpen,
  Search, Sparkles, Eye, Camera, Calendar, Loader2, X,
  TrendingUp, Filter,
} from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import Sparkline from '../Sparkline';
import BatchTrackerBar from './BatchTrackerBar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VIEW_TYPES = [
  { id: 'all', label: 'Tous' },
  { id: 'shoebox', label: 'Shoebox', color: '#D9A05B' },
  { id: 'fps', label: 'FPS', color: '#14B8A6' },
];

const PERIODS = [
  { id: 'all', label: 'Tout' },
  { id: '1', label: 'Aujourd\'hui' },
  { id: '7', label: '7 j' },
  { id: '30', label: '30 j' },
];

export default function AdminGenerationGallery({ user }) {
  const [items, setItems] = useState([]);
  const [projectsMeta, setProjectsMeta] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewType, setViewType] = useState('all');
  const [period, setPeriod] = useState('all');
  const [search, setSearch] = useState('');
  // Multi-select: Set of "type:id" strings, e.g. "project:proj_xxx", "building:bld_xxx", "floor:flr_xxx"
  // Empty set means "All"
  const [selectedNodes, setSelectedNodes] = useState(new Set());
  const [expandedProjects, setExpandedProjects] = useState({});
  const [expandedBuildings, setExpandedBuildings] = useState({});
  const [openPreview, setOpenPreview] = useState(null);
  const [timeseries, setTimeseries] = useState(null);

  const fetchTimeseries = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/generation/gallery/timeseries?days=14`, { withCredentials: true });
      setTimeseries(r.data || null);
    } catch {
      setTimeseries(null);
    }
  }, []);

  useEffect(() => { fetchTimeseries(); }, [fetchTimeseries]);

  const fetchGallery = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (viewType !== 'all') params.set('view_type', viewType);
      if (period !== 'all') params.set('days', period);
      params.set('limit', '500');
      const r = await axios.get(`${API}/generation/gallery?${params.toString()}`, { withCredentials: true });
      setItems(r.data?.items || []);
      setProjectsMeta(r.data?.projects || []);
    } catch (e) {
      console.error(e);
      toast.error('Erreur chargement galerie');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [viewType, period]);

  useEffect(() => { fetchGallery(); }, [fetchGallery]);

  // Build tree structure: project → building → floor → count
  const tree = useMemo(() => {
    const t = {};
    for (const it of items) {
      const pid = it.project_id || '__no_project__';
      const bid = it.building_id || '__no_building__';
      const fid = it.floor_id || '__no_floor__';
      if (!t[pid]) t[pid] = { name: it.project_name || 'Projet', buildings: {}, count: 0 };
      t[pid].count += 1;
      if (!t[pid].buildings[bid]) t[pid].buildings[bid] = { name: it.building_name || 'Sans bâtiment', floors: {}, count: 0 };
      t[pid].buildings[bid].count += 1;
      if (!t[pid].buildings[bid].floors[fid]) t[pid].buildings[bid].floors[fid] = { name: it.floor_name || 'Sans étage', count: 0 };
      t[pid].buildings[bid].floors[fid].count += 1;
    }
    return t;
  }, [items]);

  // Filtered list (text search + node selection — multi-select union OR)
  const filtered = useMemo(() => {
    let arr = items;
    if (selectedNodes.size > 0) {
      arr = arr.filter((i) => {
        return (
          selectedNodes.has(`project:${i.project_id}`) ||
          selectedNodes.has(`building:${i.building_id}`) ||
          selectedNodes.has(`floor:${i.floor_id}`)
        );
      });
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      arr = arr.filter((i) =>
        (i.project_name || '').toLowerCase().includes(q) ||
        (i.lot_number || '').toLowerCase().includes(q) ||
        (i.building_name || '').toLowerCase().includes(q) ||
        (i.typology || '').toLowerCase().includes(q)
      );
    }
    return arr;
  }, [items, search, selectedNodes]);

  // KPIs
  const kpis = useMemo(() => {
    const total = items.length;
    const now = Date.now();
    const weekAgo = now - 7 * 86400 * 1000;
    const week = items.filter((i) => new Date(i.completed_at || i.created_at).getTime() > weekAgo).length;
    const shoebox = items.filter((i) => (i.view_type || 'shoebox') === 'shoebox').length;
    const fps = items.filter((i) => i.view_type === 'fps').length;
    const tokensEstimate = total * 100; // 100 tokens per gen, rough
    return { total, week, shoebox, fps, tokensEstimate };
  }, [items]);

  const toggleProject = (id) => setExpandedProjects((p) => ({ ...p, [id]: !p[id] }));
  const toggleBuilding = (id) => setExpandedBuildings((p) => ({ ...p, [id]: !p[id] }));

  // Multi-select helpers
  const toggleNode = (type, id) => {
    const key = `${type}:${id}`;
    setSelectedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };
  const clearSelection = () => setSelectedNodes(new Set());
  const isSelected = (type, id) => selectedNodes.has(`${type}:${id}`);

  return (
    <div className="flex gap-4" data-testid="admin-generation-gallery" style={{ minHeight: 'calc(100vh - 240px)' }}>
      {/* LEFT TREE SIDEBAR */}
      <aside
        className="w-64 shrink-0 rounded-xl p-3 overflow-y-auto sticky top-4 self-start"
        style={{
          background: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.08)',
          maxHeight: 'calc(100vh - 180px)',
        }}
        data-testid="gallery-tree-sidebar"
      >
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xs uppercase tracking-wider font-semibold" style={{ color: 'rgba(255,255,255,0.55)' }}>
            Arborescence
          </h3>
          {user?.is_admin && (
            <span
              className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded font-semibold"
              style={{ background: 'rgba(217,160,91,0.20)', color: '#D9A05B', border: '1px solid rgba(217,160,91,0.40)' }}
              title="Mode admin: voit tous les projets"
            >
              ADMIN
            </span>
          )}
        </div>

        {/* Tous */}
        <TreeButton
          icon={Sparkles}
          label={selectedNodes.size === 0 ? 'Tous' : `Tous (${selectedNodes.size} filtre${selectedNodes.size > 1 ? 's' : ''})`}
          count={items.length}
          active={selectedNodes.size === 0}
          onClick={clearSelection}
          testid="tree-node-all"
        />

        <div className="space-y-0.5 mt-2">
          {Object.entries(tree).map(([pid, p]) => {
            const expanded = expandedProjects[pid] ?? true;
            const pActive = isSelected('project', pid);
            return (
              <div key={pid}>
                <TreeButton
                  icon={expanded ? ChevronDown : ChevronRight}
                  secondIcon={FolderOpen}
                  label={p.name}
                  count={p.count}
                  active={pActive}
                  checkbox
                  onClick={() => toggleProject(pid)}
                  onCheckboxClick={(e) => { e.stopPropagation(); toggleNode('project', pid); }}
                  testid={`tree-project-${pid}`}
                />
                {expanded && (
                  <div className="ml-3 space-y-0.5">
                    {Object.entries(p.buildings).map(([bid, b]) => {
                      const bExpanded = expandedBuildings[bid] ?? false;
                      const bActive = isSelected('building', bid);
                      return (
                        <div key={bid}>
                          <TreeButton
                            icon={bExpanded ? ChevronDown : ChevronRight}
                            secondIcon={Building2}
                            label={b.name}
                            count={b.count}
                            active={bActive}
                            checkbox
                            onClick={() => toggleBuilding(bid)}
                            onCheckboxClick={(e) => { e.stopPropagation(); toggleNode('building', bid); }}
                            testid={`tree-building-${bid}`}
                          />
                          {bExpanded && (
                            <div className="ml-3 space-y-0.5">
                              {Object.entries(b.floors).map(([fid, f]) => {
                                const fActive = isSelected('floor', fid);
                                return (
                                  <TreeButton
                                    key={fid}
                                    icon={Layers}
                                    label={f.name}
                                    count={f.count}
                                    active={fActive}
                                    checkbox
                                    onClick={() => toggleNode('floor', fid)}
                                    onCheckboxClick={(e) => { e.stopPropagation(); toggleNode('floor', fid); }}
                                    testid={`tree-floor-${fid}`}
                                  />
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {items.length === 0 && !loading && (
          <p className="text-[11px] mt-3" style={{ color: 'rgba(255,255,255,0.4)' }}>
            Aucune génération
          </p>
        )}
      </aside>

      {/* MAIN VIEW */}
      <div className="flex-1 min-w-0">
        {/* Batch tracker bar — affiche les batches en cours/récemment terminés */}
        <BatchTrackerBar onJobDone={fetchGallery} />

        {/* Sticky KPI bar */}
        <div
          className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3 sticky top-0 z-10 backdrop-blur-md p-2 rounded-xl"
          style={{ background: 'rgba(10,10,13,0.85)', border: '1px solid rgba(255,255,255,0.08)' }}
          data-testid="gallery-kpis"
        >
          <Kpi
            label="Total · 14j"
            value={kpis.total}
            icon={Sparkles}
            color="#D9A05B"
            spark={timeseries?.total}
            testid="kpi-total"
          />
          <Kpi label="Cette semaine" value={kpis.week} icon={TrendingUp} color="#22C55E" testid="kpi-week" />
          <Kpi
            label="Shoebox"
            value={kpis.shoebox}
            icon={Eye}
            color="#D9A05B"
            spark={timeseries?.shoebox}
            testid="kpi-shoebox"
          />
          <Kpi
            label="FPS"
            value={kpis.fps}
            icon={Camera}
            color="#14B8A6"
            spark={timeseries?.fps}
            testid="kpi-fps"
          />
        </div>

        {/* Filters bar */}
        <div className="flex items-center gap-2 flex-wrap mb-4" data-testid="gallery-filters">
          <FilterGroup
            label="Type"
            value={viewType}
            onChange={setViewType}
            options={VIEW_TYPES}
            testidPrefix="filter-viewtype"
          />
          <FilterGroup
            label="Période"
            icon={Calendar}
            value={period}
            onChange={setPeriod}
            options={PERIODS}
            testidPrefix="filter-period"
          />
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2"
              style={{ color: 'rgba(255,255,255,0.4)' }} />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Recherche projet / lot / typo"
              className="pl-8 h-8 text-xs"
              data-testid="gallery-search"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2"
                title="Effacer"
              >
                <X className="w-3.5 h-3.5" style={{ color: 'rgba(255,255,255,0.4)' }} />
              </button>
            )}
          </div>
          {(selectedNodes.size > 0 || viewType !== 'all' || period !== 'all' || search) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                clearSelection();
                setViewType('all');
                setPeriod('all');
                setSearch('');
              }}
              data-testid="gallery-reset-filters"
              className="text-xs"
            >
              <Filter className="w-3 h-3 mr-1" />
              Reset
            </Button>
          )}
        </div>

        {/* Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin" style={{ color: '#D9A05B' }} />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 rounded-xl"
            style={{ background: 'rgba(255,255,255,0.02)', border: '1px dashed rgba(255,255,255,0.10)' }}
            data-testid="gallery-empty">
            <Sparkles className="w-10 h-10 mx-auto mb-3" style={{ color: 'rgba(255,255,255,0.25)' }} />
            <p className="text-sm" style={{ color: 'rgba(255,255,255,0.65)' }}>Aucune génération pour ces filtres</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="gallery-grid">
            {filtered.map((it) => (
              <GalleryCard key={it.job_id} item={it} onPreview={() => setOpenPreview(it)} />
            ))}
          </div>
        )}
      </div>

      {/* Preview modal */}
      {openPreview && (
        <PreviewModal item={openPreview} onClose={() => setOpenPreview(null)} />
      )}
    </div>
  );
}

// ------- Sub-components -------
function TreeButton({ icon: Icon, secondIcon: SecondIcon, label, count, active, checkbox, onClick, onCheckboxClick, testid }) {
  return (
    <div
      className="w-full flex items-center gap-1.5 px-2 py-1 rounded text-xs text-left transition-colors"
      style={{
        background: active ? 'rgba(217,160,91,0.15)' : 'transparent',
        color: active ? '#E8B26C' : 'rgba(255,255,255,0.7)',
        border: `1px solid ${active ? 'rgba(217,160,91,0.40)' : 'transparent'}`,
      }}
    >
      {checkbox && (
        <button
          onClick={onCheckboxClick}
          data-testid={`${testid}-checkbox`}
          className="w-3.5 h-3.5 shrink-0 rounded flex items-center justify-center transition-all"
          style={{
            background: active ? '#D9A05B' : 'transparent',
            border: `1.5px solid ${active ? '#D9A05B' : 'rgba(255,255,255,0.35)'}`,
          }}
          title={active ? 'Désélectionner' : 'Sélectionner'}
        >
          {active && (
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
              <path d="M2 5L4 7L8 3" stroke="#000" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </button>
      )}
      <button
        onClick={onClick}
        data-testid={testid}
        className="flex items-center gap-1.5 flex-1 min-w-0"
      >
        <Icon className="w-3 h-3 shrink-0" />
        {SecondIcon && <SecondIcon className="w-3 h-3 shrink-0" />}
        <span className="truncate flex-1 text-left">{label}</span>
        <span
          className="text-[10px] px-1 rounded tabular-nums"
          style={{
            background: active ? 'rgba(217,160,91,0.20)' : 'rgba(255,255,255,0.06)',
            color: active ? '#E8B26C' : 'rgba(255,255,255,0.5)',
          }}
        >
          {count}
        </span>
      </button>
    </div>
  );
}

function Kpi({ label, value, icon: Icon, color, spark, testid }) {
  return (
    <div
      className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg"
      style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}
      data-testid={testid}
    >
      <div
        className="w-7 h-7 rounded-md flex items-center justify-center shrink-0"
        style={{ background: `${color}22`, border: `1px solid ${color}44` }}
      >
        <Icon className="w-3.5 h-3.5" style={{ color }} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[9px] uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.45)' }}>{label}</p>
        <p className="text-lg font-bold tabular-nums leading-tight" style={{ color }}>{value}</p>
      </div>
      {spark && spark.length > 1 && (
        <div className="w-16 shrink-0" data-testid={`${testid}-spark`}>
          <Sparkline data={spark} color={color} height={28} />
        </div>
      )}
    </div>
  );
}

function FilterGroup({ label, icon: Icon, value, onChange, options, testidPrefix }) {
  return (
    <div className="flex items-center gap-1">
      {Icon && <Icon className="w-3 h-3" style={{ color: 'rgba(255,255,255,0.4)' }} />}
      <span className="text-[10px] uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.4)' }}>
        {label}
      </span>
      <div className="flex items-center gap-0.5">
        {options.map((o) => (
          <button
            key={o.id}
            onClick={() => onChange(o.id)}
            data-testid={`${testidPrefix}-${o.id}`}
            className="px-2 py-1 rounded text-[10px] transition-colors"
            style={{
              background: value === o.id ? (o.color ? `${o.color}25` : 'rgba(217,160,91,0.18)') : 'rgba(255,255,255,0.04)',
              color: value === o.id ? (o.color || '#E8B26C') : 'rgba(255,255,255,0.55)',
              border: `1px solid ${value === o.id ? (o.color ? `${o.color}66` : 'rgba(217,160,91,0.45)') : 'rgba(255,255,255,0.08)'}`,
            }}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function GalleryCard({ item, onPreview }) {
  const isFps = item.view_type === 'fps';
  return (
    <button
      onClick={onPreview}
      className="group rounded-xl overflow-hidden border text-left transition-all hover:scale-[1.01]"
      style={{ borderColor: 'rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.02)' }}
      data-testid={`gallery-card-${item.job_id}`}
    >
      <div className="aspect-square overflow-hidden relative" style={{ background: 'rgba(0,0,0,0.4)' }}>
        <img
          src={`${process.env.REACT_APP_BACKEND_URL}${item.image_url}`}
          alt={item.lot_number || item.job_id}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <span
          className="absolute top-2 left-2 px-1.5 py-0.5 rounded text-[9px] font-medium uppercase tracking-wider"
          style={{ background: isFps ? 'rgba(20,184,166,0.85)' : 'rgba(217,160,91,0.85)', color: '#000' }}
        >
          {isFps ? `FPS · ${item.fov_deg || 75}°` : 'Shoebox'}
        </span>
      </div>
      <div className="px-2.5 py-2">
        <p className="text-[10px] uppercase tracking-wider truncate" style={{ color: 'rgba(255,255,255,0.5)' }}>
          {item.project_name || 'Projet'}
        </p>
        <p className="text-xs font-medium truncate" style={{ color: 'var(--c-text-1, #fff)' }}>
          {item.lot_number || 'Lot'} · {item.typology || '—'}
        </p>
        <div className="flex items-center gap-1.5 mt-0.5 text-[10px]" style={{ color: 'rgba(255,255,255,0.40)' }}>
          {item.building_name && <span className="truncate">{item.building_name}</span>}
          {item.floor_name && <span className="truncate">· {item.floor_name}</span>}
        </div>
        <p className="text-[10px] mt-1" style={{ color: 'rgba(255,255,255,0.40)' }}>
          {new Date(item.completed_at || item.created_at).toLocaleDateString('fr-FR')}
        </p>
      </div>
    </button>
  );
}

function PreviewModal({ item, onClose }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-6"
      style={{ background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)' }}
      onClick={onClose}
      data-testid="gallery-preview-modal"
    >
      <div
        className="max-w-5xl w-full rounded-xl overflow-hidden"
        style={{ background: '#0a0a0d', border: '1px solid rgba(255,255,255,0.10)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <div>
            <p className="text-sm font-semibold" style={{ color: '#fff' }}>{item.lot_number} · {item.typology}</p>
            <p className="text-xs" style={{ color: 'rgba(255,255,255,0.5)' }}>
              {item.project_name} › {item.building_name} › {item.floor_name}
            </p>
          </div>
          <button onClick={onClose} className="p-2 rounded hover:bg-white/10" data-testid="gallery-preview-close">
            <X className="w-5 h-5 text-white/70" />
          </button>
        </div>
        <img
          src={`${process.env.REACT_APP_BACKEND_URL}${item.image_url}`}
          alt={item.job_id}
          className="w-full max-h-[80vh] object-contain"
        />
      </div>
    </div>
  );
}
